import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import {
    DaCheckboxField,
    DaNumberFormatField, NumberFormatType, DaSignatureField, DaTextField,
    Form, FormSectionHeader, HTMLComment,
} from "../_shared";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import { pafApi, IPaf } from "../../_api";

interface PafSignViewParams {
    token: string;
}

export const PafSignView: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const params = useParams<keyof PafSignViewParams>();
    const token = params.token || '';

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [pafData, setPafData] = useState<IPaf | null>(null);
    const [signatureText, setSignatureText] = useState<string>('');
    const [signatureAttest, setSignatureAttest] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [alreadySigned, setAlreadySigned] = useState<boolean>(false);
    const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
    const successAlertRef = useRef<HTMLDivElement>(null);

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    const { control, formState: { errors }, reset } = useForm<IPaf>({
        defaultValues: pafData || {},
        mode: "onChange",
    });

    useEffect(() => {
        if (pafData) {
            reset(pafData);
        }
    }, [pafData, reset]);

    useEffect(() => {
        if (submitSuccess && successAlertRef.current) {
            successAlertRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [submitSuccess]);

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError('Invalid or missing token');
                setLoading(false);
                return;
            }

            try {
                const response = await pafApi.sign_verify(token);
                if (response?.success && response?.data) {
                    setPafData(response.data);
                    // Check if already signed
                    if (response.data.list_admin_signature_attest && response.data.list_admin_signature_text) {
                        setSignatureText(response.data.list_admin_signature_text);
                        setSignatureAttest(true);
                        setAlreadySigned(true);
                    }
                } else {
                    setError(response?.message || 'Failed to verify token');
                }
            } catch (e: any) {
                setError(e.message || 'An error occurred while verifying the token');
            } finally {
                setLoading(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleSubmitSignature = async () => {
        if (!signatureText.trim()) {
            setError('Please enter your signature');
            return;
        }

        if (!signatureAttest) {
            setError('Please check the attestation checkbox to confirm');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await pafApi.sign_submit(token, {
                list_admin_signature_attest: signatureAttest,
                list_admin_signature_text: signatureText,
            });

            if (response?.success) {
                setSubmitSuccess(true);
                setPafData(response.data);
            } else {
                setError(response?.message || 'Failed to submit signature');
            }
        } catch (e: any) {
            setError(e.message || 'An error occurred while submitting the signature');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                sx={{ backgroundColor: theme.palette.background.default }}
            >
                <Box textAlign="center">
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Verifying access...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error && !pafData) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="100vh"
                sx={{ backgroundColor: theme.palette.background.default, p: 3 }}
            >
                <Paper elevation={3} sx={{ p: 4, maxWidth: 500, width: '100%' }}>
                    <Alert severity="error">
                        <AlertTitle>Access Denied</AlertTitle>
                        {error}
                    </Alert>
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            If you believe this is an error, please contact the person who sent you this link.
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default,
                py: 4,
                px: 2,
            }}
        >
            <Box maxWidth="900px" mx="auto">
                {/* Header */}
                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h4" gutterBottom color="primary">
                        daxPAF - Digital PAF Signing
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        An automated Digital PAF Management Platform (Powered by Data Axle)
                    </Typography>
                </Paper>

                {submitSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }} ref={successAlertRef}>
                        <AlertTitle>Successfully Signed!</AlertTitle>
                        The PAF has been signed successfully. You can close this window.
                    </Alert>
                )}

                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* PAF Details */}
                {pafData && (
                    <Grid container>
                        <Grid item xs={12}>
                            <Form
                                autoComplete="off"
                            >
                                <Box sx={{ minHeight: theme.spacing(73) }}>
                                    {/* Paf Info Tab */}
                                    <Grid container spacing={theme.spacing(1)}>
                                        {/* List Owner Section Header */}
                                        <Grid item xs={12} md={12}>
                                            <FormSectionHeader
                                                title={"List Owner"}
                                                typeVariant={"h5"}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__company_name"}
                                                label={"Company Name"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__address1"}
                                                label={"Address 1"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__address2"}
                                                label={"Address 2"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DaTextField
                                                control={control}
                                                name={"company__city"}
                                                label={"City"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"company__state"}
                                                label={"State"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <DaTextField
                                                control={control}
                                                name={"company__postal_code"}
                                                label={"Zip"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DaTextField
                                                control={control}
                                                name={"company__urbanization"}
                                                label={"Urbanization"}
                                                required={false}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"company__phone"}
                                                label={"Phone"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <HTMLComment text={"Placeholder"} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"company__naics_code"}
                                                label={"NAICS Code"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"company__usps_mailer_id"}
                                                label={"USPS Mailer ID"}
                                                required={false}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"company__company_registration_id"}
                                                label={"Customer Registration ID"}
                                                required={false}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__representative_name"}
                                                label={"Representative Name"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__representative_title"}
                                                label={"Representative Title"}
                                                required={false}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__representative_email"}
                                                label={"Representative Email"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"company__company_website"}
                                                label={"Company Website"}
                                                required={false}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaCheckboxField
                                                control={control}
                                                name={"is_ncoa"}
                                                disabled={true}
                                                label={<>NCOA<sup>Link</sup> Service</>}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaCheckboxField
                                                control={control}
                                                name={"is_dsf"}
                                                disabled={true}
                                                label={<>DSF<sup>2</sup> Service</>}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaCheckboxField
                                                control={control}
                                                name={"is_waiver_on_file"}
                                                disabled={true}
                                                label={<>Waive NCOA<sup>Link</sup> 7-Day Fulfillment</>}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"company__customer_since_date"}
                                                label={"Customer Since Date"}
                                                required={false}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                    </Grid>
                                    {/* List Admin Tab */}
                                    <Grid container spacing={theme.spacing(1)}>
                                        <Grid item xs={12} md={12}>
                                            <FormSectionHeader
                                                title={"Broker / Agent / List Admin"}
                                                typeVariant={"h5"}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <HTMLComment text={"Placeholder"} />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__company_name"}
                                                label={"Company Name"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__address1"}
                                                label={"Address 1"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__address2"}
                                                label={"Address 2"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__city"}
                                                label={"City"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__state"}
                                                label={"State"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__postal_code"}
                                                label={"Zip"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaTextField
                                                control={control}
                                                name={"listadmin__phone"}
                                                label={"Phone"}
                                                required={true}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}> </Grid>
                                        <Grid item xs={12} md={4}>
                                            <HTMLComment text={"Placeholder"} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaSignatureField
                                                control={control}
                                                name={"licensee_signature_text"}
                                                label={"LIST OWNER Signature"}
                                                readOnly={true}
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                    </Grid>
                                    {/* Licensee Tab */}
                                    <Grid container spacing={theme.spacing(1)}>
                                        <Grid item xs={12} md={12}>
                                            <FormSectionHeader
                                                title={"Licensee"}
                                                typeVariant={"h5"}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"licensee__company_name"}
                                                label={"Company Name"}
                                                readOnly
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"licensee__officer_name"}
                                                label={"Officer Name"}
                                                readOnly
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <DaTextField
                                                control={control}
                                                name={"licensee__officer_title"}
                                                label={"Officer Title"}
                                                readOnly
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <DaNumberFormatField
                                                control={control}
                                                name={"licensee__federal_tax_id"}
                                                label={"Federal Tax ID"}
                                                readOnly
                                                variant={formFieldVariant}
                                                size={formFieldSize}
                                                errors={errors}
                                                numberFormat={NumberFormatType.EIN}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Form>
                        </Grid>
                        {/* Signature Section */}
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Broker Signature
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {submitSuccess || alreadySigned ? (
                                <Box>
                                    {submitSuccess ? (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            "This PAF has been signed successfully."
                                        </Alert>
                                    ) : (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            "This PAF has already been signed."
                                        </Alert>
                                    )}
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Signature
                                            </Typography>
                                            <Typography variant="body1">
                                                {pafData.listadmin_signature_text || signatureText}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Date Signed
                                            </Typography>
                                            <Typography variant="body1">
                                                {pafData.listadmin_signature_date
                                                    ? new Date(pafData.listadmin_signature_date).toLocaleString()
                                                    : new Date().toLocaleString()}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        By signing below, you certify that you have reviewed the PAF information 
                                        and agree to the terms and conditions.
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        label="Type your full name as signature"
                                        value={signatureText}
                                        onChange={(e) => setSignatureText(e.target.value)}
                                        variant="outlined"
                                        sx={{ mb: 2 }}
                                        disabled={submitting}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={signatureAttest}
                                                onChange={(e) => setSignatureAttest(e.target.checked)}
                                                disabled={submitting}
                                            />
                                        }
                                        label="I certify that the information provided is accurate and I agree to the terms and conditions of this PAF."
                                        sx={{ mb: 3 }}
                                    />

                                    <Box textAlign="right">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            onClick={handleSubmitSignature}
                                            disabled={submitting || !signatureText.trim() || !signatureAttest}
                                        >
                                            {submitting ? (
                                                <>
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                    Signing...
                                                </>
                                            ) : (
                                                'Sign PAF'
                                            )}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                )}

                {/* Footer */}
                <Box textAlign="center" sx={{ mt: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                        daxPAF – An automated Digital PAF Management Platform (Powered by Data Axle)
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

